import React, {useCallback, useEffect, useRef} from 'react';
import { TableOfContentsItemProps, TableOfContentsProps } from './ContentPanel';
import Anchor from 'antd/lib/anchor';
import { Panel } from '../../components/layout/panel';

const getHref = (id?: string) => {
   if (id) {
      if (id.indexOf('#') != 0) {
         return '#' + id;
      }
   }
   return id;
};

const createAnchorLinksFromItems = (items: TableOfContentsItemProps[]) => {

   const result = [];

   let index;
   const numItems = items.length;
   for (index = 0; index < numItems; index++) {
      const item: TableOfContentsItemProps | undefined = items[index];
      if (item.items !== undefined) {
         result.push(<Anchor.Link href={getHref(item.id)} title={item.title} key={item.id}>
            {createAnchorLinksFromItems(item.items)}
         </Anchor.Link>);
      }
      else {
         result.push(<Anchor.Link href={getHref(item.id)} title={item.title} key={item.id}/>);
      }
   }
   return result;
};

const findItemWithKey = (items: TableOfContentsItemProps[],
                         id: string): TableOfContentsItemProps[] | undefined => {

   let index;
   const numItems = items.length;
   for (index = 0; index < numItems; index++) {
      const item: TableOfContentsItemProps | undefined = items[index];
      if (item.id === id) {
         return [item];
      }
      if (item.items) {
         const subItemArray = findItemWithKey(item.items, id);
         if (subItemArray !== undefined) {
            return [item].concat(subItemArray);
         }
      }
   }
   return undefined;
};

export const TableOfContents = (props: TableOfContentsProps & { scrollContainer?: () => HTMLElement | Window }) => {

   const { selectedId, onClick, items } = props;

   const tocRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      setTimeout(()=> {
         if (selectedId !== undefined && tocRef !== undefined && tocRef.current !== null) {
            const anchor: HTMLAnchorElement | null = tocRef.current.querySelector('a[href^="#'+selectedId+'"]');
            if (anchor !== null) {

               // Don't smoothly animate and do this twice to get around some issue in chrome scrollIntoView
               anchor.scrollIntoView({
                                        block: "start"
                                     });
               setTimeout(() => {
                  anchor.click();

                  // Don't smoothly animate and do this twice to get around some issue in chrome scrollIntoView
                  anchor.scrollIntoView({
                                           block: "start"
                                        });
               }, 150);
            }
         }
      }, 100);
   }, [tocRef, selectedId]);

   const handleClick = useCallback((
      event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
      link: {
         title: React.ReactNode;
         href: string;
      },
   ) => {
      event.preventDefault();
      const curKey = link.href.substring(1);
      const itemPath = findItemWithKey(items, curKey);
      if (itemPath !== undefined) {
         const selectedItem = itemPath[itemPath.length - 1];
         const clickInfo = {
            id: selectedItem.id,
            idPath: itemPath.map((item: TableOfContentsItemProps) => item.id),
            item: selectedItem,
            domEvent: event.nativeEvent
         };
         if (selectedItem.onClick !== undefined) {
            selectedItem.onClick(clickInfo);
         }
         if (onClick) {
            onClick(clickInfo);
         }
      }
   },[onClick, items]);

   const handleOnChange = useCallback((curActiveLink: string) => {
      setTimeout(()=> {
         if (tocRef && tocRef.current) {
            const activeLink = tocRef.current.getElementsByClassName('ant-anchor-link-title-active');
            if (activeLink && activeLink.length > 0) {
               const firstItem = activeLink.item(0);
               if (firstItem) {
                  firstItem.scrollIntoView({
                                              behavior: "smooth",
                                              block: "start"
                                           });
               }
            }
         }
      }, 150);
   },[]);

   return <Panel className='uf-table-of-contents' horizontalFill={false} paddingLeft={true} paddingRight={true} ref={tocRef} width={170} height={'100%'}>
      <Anchor affix={false}
              showInkInFixed={true}
              onClick={handleClick}
              getContainer={props.scrollContainer}
              onChange={handleOnChange}
              targetOffset={16}
      >
          {createAnchorLinksFromItems(props.items)}
      </Anchor>
   </Panel>;
};
